import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Info, CheckCircle2 } from 'lucide-react';
import { WEEKDAYS, COURSE_TYPES, CLASS_TYPES, CLASS_LANGUAGES, LOCATIONS } from '@/lib/education-levels';
import { CreateTemplateFormData } from './types';

interface StepSummaryProps {
  formData: CreateTemplateFormData;
}

export function StepSummary({ formData }: StepSummaryProps) {
  const getWeekdayLabel = (weekday: number) => 
    WEEKDAYS.find(d => d.value === weekday)?.label || weekday;
  
  const getCourseTypeLabel = (type: string) =>
    COURSE_TYPES.find(t => t.value === type)?.label || type;
  
  const getClassTypeLabel = (type: string) =>
    CLASS_TYPES.find(t => t.value === type)?.label || type;
  
  const getLanguageLabel = (lang: string) =>
    CLASS_LANGUAGES.find(l => l.value === lang)?.label || lang;
  
  const getLocationLabel = (loc: string) =>
    LOCATIONS.find(l => l.value === loc)?.label || loc;

  return (
    <div className="space-y-6">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>สรุปข้อมูล</AlertTitle>
        <AlertDescription>
          กรุณาตรวจสอบข้อมูลก่อนยืนยันการสร้างตารางเรียน
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-primary" />
            ครูและนักเรียน
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground">ครูผู้สอน</p>
            {formData.teacher_info ? (
              <p className="font-medium">
                {formData.teacher_info.code} - {formData.teacher_info.nickname || formData.teacher_info.firstName} 
                ({formData.teacher_info.firstName} {formData.teacher_info.lastName})
              </p>
            ) : (
              <p className="text-destructive">ยังไม่ได้เลือกครู</p>
            )}
          </div>
          
          <div>
            <p className="text-sm text-muted-foreground">นักเรียน ({formData.students.length} คน)</p>
            {formData.students.length > 0 ? (
              <div className="flex flex-wrap gap-2 mt-1">
                {formData.students.map((s) => (
                  <Badge key={s.code} variant="secondary">
                    {s.code} - {s.nickname || s.first_name}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground">ยังไม่ได้เลือกนักเรียน</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-primary" />
            ตารางเวลาเรียน ({formData.schedules.length} คลาส)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {formData.schedules.map((schedule, index) => (
              <div key={index} className="flex items-center gap-2 p-2 bg-muted rounded">
                <Badge variant="outline">คลาส {index + 1}</Badge>
                <span>
                  วัน{getWeekdayLabel(schedule.weekday)} เวลา {schedule.start_time} - {schedule.end_time}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-primary" />
            ข้อมูลคลาสเรียน
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">วิชา</p>
              <p className="font-medium">{formData.subject || '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">ระดับชั้น</p>
              <p className="font-medium">{formData.education_level || '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">ประเภทคอร์ส</p>
              <p className="font-medium">{getCourseTypeLabel(formData.course_type)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">ประเภทคลาส</p>
              <p className="font-medium">{getClassTypeLabel(formData.class_type)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">ภาษา</p>
              <p className="font-medium">{getLanguageLabel(formData.language)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-primary" />
            อื่นๆ
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">สาขา</p>
              <p className="font-medium">{getLocationLabel(formData.location) || '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">ห้องเรียน</p>
              <p className="font-medium">{formData.room_name || '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">คลาสพิเศษ</p>
              <p className="font-medium">
                {formData.is_special_class 
                  ? `ใช่ (${formData.fixed_cost?.toLocaleString() || 0} บาท/ชม.)` 
                  : 'ไม่ใช่'}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">หมายเหตุ</p>
              <p className="font-medium">{formData.note || '-'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Alert className="border-primary bg-primary/5">
        <Info className="h-4 w-4 text-primary" />
        <AlertTitle className="text-primary">จะสร้างตารางเรียนทั้งหมด {formData.schedules.length} รายการ</AlertTitle>
        <AlertDescription>
          นักเรียนและข้อมูลคลาสเรียนจะเหมือนกันทุกรายการ แต่วันและเวลาจะแตกต่างกันตามที่กำหนด
        </AlertDescription>
      </Alert>
    </div>
  );
}
